import { translateBackend } from '@assets/i18n/config';
import ViewAllProfilesIcon from '@assets/images/icons-filled/view-all-profiles.svg';
import { AuthenticationConnections } from '@interfaces/organization';
import { FeatureFlagTypes } from '@interfaces/organizationFeatureFlag';
import {
  OrganizationUserAccessTypes,
  OrganizationUserEntityRelationTypes,
} from '@interfaces/organizationUser';
import { NavigationStyles } from '@interfaces/role';
import PersonIcon from '@mui/icons-material/AccountCircle';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import LanguageIcon from '@mui/icons-material/Language';
import LaunchIcon from '@mui/icons-material/Launch';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import Person from '@mui/icons-material/Person';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Avatar } from '@ovComponents/2-component/avatar/avatar';
import { Divider } from '@ovComponents/2-component/divider/divider';
import { IconButton } from '@ovComponents/2-component/iconButton/iconButton';
import { Menu } from '@ovComponents/2-component/menu/menu';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { ViewAllProfilesDialog } from '@ovComponents/3-pattern/viewAllProfilesDialog/viewAllProfilesDialog';
import type { IUserEntity } from '@ovComponents/3-pattern/viewAllProfilesDialog/viewAllProfilesDialog.types';
import { CreateEntityWorkflow } from '@ovComponents/4-module/createEntityWorkflow/createEntityWorkflow';
import { MemberTag } from '@pages/household/components/memberTag';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { useGlobalToast } from '@providers/globalToastProvider';
import { useThemeTokens } from '@providers/themeTokenProvider';
import {
  type ActiveEntityProps,
  UserContext,
  usePermissions,
} from '@providers/userContextProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { entityInitials, entityName, isFeatureEnabled } from '@utils/common';
import filterHouseholdsWithAccess from '@utils/filterHouseholdsWithAccess';
import { safeWindowOpen } from '@utils/urlSanitization';
import { usePageState, useSearchParams } from '@utils/usePageState';
import React, {
  type FC,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChangeAvatarModal } from '../changeAvatarModal/changeAvatarModal';
import { LanguageSelectionModal } from '../languageSelectionModal/languageSelectionModal';
import { LoginSettingModal } from '../loginSettingModal/loginSettingModal';
import { NotificationPreferencesModal } from '../notificationPreferencesModal/notificationPreferencesModal';
import { useEntityMenuTokens } from './entityMenu.tokens';
import { colors, colorsInner } from './entityMenuColors';

const MAX_PROFILES_IN_MENU = 4;

type IEntityMenuProps = {
  logout: () => void;
  showOnlyLogout?: boolean;
};

const EntityMenu: FC<IEntityMenuProps> = ({ logout, showOnlyLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { t } = useTranslation(['entityMenu', 'navMenu', 'shared']);
  const { showToast } = useGlobalToast();
  const { sendAnalytic } = useContext(AnalyticsContext);
  const { permissions } = usePermissions();
  const { comp } = useThemeTokens(useEntityMenuTokens());
  const {
    userContext,
    activeEntity,
    activeHousehold,
    activeOrganization,
    setActiveEntity,
    setActiveHousehold,
  } = useContext(UserContext);

  const [createEntityOpen, setCreateEntityOpen] = usePageState(
    false,
    'createEntity',
  );
  const [updateLanguageOpen, setUpdateLanguageOpen] = useState(false);
  const [
    updateNotificationPreferencesOpen,
    setUpdateNotificationPreferencesOpen,
  ] = useState(false);
  const [updateAvatarOpen, setUpdateAvatarOpen] = useState(false);
  const [viewAllProfiles, setViewAllProfiles] = useState(false);
  const [autoOpenWorkflow, setAutoOpenWorkflow] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [loginSettingsOpen, setLoginSettingsOpen] = useState(false);

  const open = Boolean(anchorEl);
  const entities: IUserEntity[] =
    userContext.accessType === OrganizationUserAccessTypes.REP_CODE
      ? (userContext?.repCodeEntities ?? [])
      : (userContext?.entities ?? []);
  const nonAffiliateEntities = entities.filter((x) => !x.entity.affiliateOnly);
  const autoOpenInParams = params.get('autoOpenWorkflow');

  let allowLogout = activeOrganization.allowLogout ?? true;
  if (
    userContext.organization &&
    activeOrganization.id !== userContext.organization.id
  ) {
    allowLogout = true;
  }

  const handleClose = () => setAnchorEl(null);
  const handleClick = (event: any) => {
    sendAnalytic(ovAnalyticsEvents.homepageProfileSelect);
    setAnchorEl(event.currentTarget);
  };

  useEffect(() => {
    if (
      !activeEntity ||
      !activeEntity.id ||
      !(userContext.role?.navigationStyle === NavigationStyles.SIMPLE)
    )
      return;

    let path = '';
    const households = filterHouseholdsWithAccess(
      activeEntity?.households || [],
      activeEntity?.id,
      userContext,
    );
    const isHouseholdDefaultView =
      households.length > 0 && userContext.role?.makeHouseholdDefaultView;

    if (
      isHouseholdDefaultView &&
      activeHousehold?.id &&
      (!location.pathname.includes('households') ||
        !location.pathname.includes(activeHousehold?.id))
    ) {
      path = `/households/${activeHousehold?.id}`;
    } else if (
      !isHouseholdDefaultView &&
      !location.pathname.includes(activeEntity.id || '')
    ) {
      path = `/clients/${activeEntity?.id}`;
    }

    if (path) {
      if (autoOpenWorkflow || autoOpenInParams) {
        // Sets the autoOpenWorkflow only when initially loading the page for the first entity
        navigate(`${path}?autoOpenWorkflow=true`);
        setAutoOpenWorkflow(false);
      } else {
        navigate(path);
      }
    }
  }, [activeEntity, navigate, activeHousehold, userContext]);

  const isUnifiedClientExperience =
    userContext.role?.navigationStyle === NavigationStyles.SIMPLE;

  const profileName = useCallback(
    (entity?: ActiveEntityProps) => entityName(entity) || entity?.primaryEmail,
    [],
  );
  const profileInitials = useCallback(
    (entity?: ActiveEntityProps) => entityInitials(entity),
    [],
  );

  const getGroupedEntitiesSortedByType = (type?: string) => {
    const isIndividual = type?.toUpperCase() === 'INDIVIDUAL';

    const filtered = nonAffiliateEntities.filter((item) =>
      isIndividual
        ? item.entity.type === 'INDIVIDUAL'
        : item.entity.type !== 'INDIVIDUAL',
    );

    const sorted = [...filtered].sort((a, b) =>
      profileName(a.entity).localeCompare(profileName(b.entity)),
    );

    const active = sorted.filter((item) => item.entity.id === activeEntity?.id);
    const nonActive = sorted.filter(
      (item) => item.entity.id !== activeEntity?.id,
    );

    return [...active, ...nonActive];
  };

  const handleEntitySelect = (item: IUserEntity) => {
    handleClose();
    const households = filterHouseholdsWithAccess(
      item.entity?.households || [],
      item.entity?.id,
      userContext,
    );
    const isHouseholdDefaultView =
      !!userContext.role?.makeHouseholdDefaultView && households.length > 0;
    if (isHouseholdDefaultView) {
      const household = households[0];
      setActiveHousehold(household);
      navigate(`/households/${household?.id}/clients/${item.entity.id}`);
    }
    setActiveEntity(item.entity);
  };

  const handleUrlItem = (analyticEvent: string, url: string) => {
    sendAnalytic(analyticEvent);
    handleClose();
    safeWindowOpen(url, {
      target: '_blank',
      onError: (errorMsg) => {
        showToast({ severity: 'error', message: errorMsg });
      },
    });
  };

  const individualEntities = getGroupedEntitiesSortedByType('individual');
  const nonIndividualEntities = getGroupedEntitiesSortedByType();

  const activeEntityItem = nonAffiliateEntities.find(
    (en) => en.entity.id === activeEntity?.id,
  ) as IUserEntity;
  const totalEntitiesCount =
    individualEntities.length + nonIndividualEntities.length;

  return (
    <>
      <IconButton onClick={handleClick} data-testid='profile-menu-icon'>
        <span>
          <Avatar
            state='highlight'
            size='large'
            fgColor={comp.entityMenu.avatar.foregroundColor}
            bgColor={comp.entityMenu.avatar.backgroundColor}
            avatar={userContext.avatar}
          >
            {(activeEntity && isUnifiedClientExperience
              ? (profileInitials(activeEntity) ?? '')
              : `${userContext.firstName?.[0]}${userContext.lastName?.[0]}`
            )?.toUpperCase()}
          </Avatar>
        </span>
      </IconButton>
      <Menu
        id='profile-menu'
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        disableScrollLock={true}
        sx={{
          '.MuiPaper-root': {
            boxShadow: '0 0 8px rgba(0,0,0,0.1)',
            width: '230px',
            borderRadius: comp.entityMenu.borderRadius,
          },
        }}
      >
        {!showOnlyLogout &&
          isUnifiedClientExperience && [
            ...(totalEntitiesCount <= MAX_PROFILES_IN_MENU
              ? [
                  ...individualEntities.map((en, index) => (
                    <EntityMenuItem
                      item={en}
                      isActive={en.entity.id === activeEntity?.id}
                      index={index}
                      onClick={() => handleEntitySelect(en)}
                    />
                  )),
                  ...(individualEntities.length > 0 &&
                  nonIndividualEntities.length > 0
                    ? [<Divider />]
                    : []),
                  ...(nonIndividualEntities.length > 0
                    ? [
                        <Box sx={{ px: 2, py: 1 }}>
                          <Typography
                            variant='labelSmall'
                            fontSize='10px'
                            colorVariant='variant'
                          >
                            {t(
                              'navMenu:menu.nonIndividualClients',
                            ).toUpperCase()}
                          </Typography>
                        </Box>,
                        ...nonIndividualEntities.map((en, index) => (
                          <EntityMenuItem
                            item={en}
                            isActive={en.entity.id === activeEntity?.id}
                            index={index}
                            onClick={() => handleEntitySelect(en)}
                          />
                        )),
                      ]
                    : []),
                ]
              : []),

            ...(totalEntitiesCount > MAX_PROFILES_IN_MENU
              ? [
                  <EntityMenuItem
                    item={activeEntityItem}
                    isActive
                    index={0}
                    onClick={() => handleEntitySelect(activeEntityItem)}
                  />,
                  <MenuItemWithImage
                    image={ViewAllProfilesIcon}
                    title={t('viewAllProfiles')}
                    testId='view-all-profile'
                    onClick={() => {
                      handleClose();
                      setViewAllProfiles(true);
                    }}
                  />,
                ]
              : []),
            <Divider />,
            ...(isFeatureEnabled(FeatureFlagTypes.ALLOW_ADDING_A_NEW_PROFILE)
              ? [
                  <MenuItemWithIcon
                    MuiSvgIcon={AddCircleOutlineIcon}
                    title={t('addNewEntity')}
                    testId='add-new-entity'
                    onClick={() => {
                      handleClose();
                      setCreateEntityOpen(true);
                    }}
                  />,
                ]
              : []),
          ]}
        {permissions.includes('write:organization_user_profile') && (
          <MenuItemWithIcon
            MuiSvgIcon={LanguageIcon}
            title={t('language')}
            testId='menuitem-language'
            onClick={() => {
              handleClose();
              setUpdateLanguageOpen(true);
            }}
          />
        )}
        {activeOrganization.enableLoginSettings &&
          (activeOrganization.defaultAuthenticationConnection !==
            AuthenticationConnections.EMAIL_OTP ||
            activeOrganization.loginUrl) && (
            <MenuItemWithIcon
              MuiSvgIcon={Person}
              title={translateBackend(activeOrganization.loginSettingsTitle)}
              testId='menuitem-loginSettings'
              onClick={() => {
                handleClose();

                const { defaultCustodianConnection } = activeOrganization;
                if (
                  defaultCustodianConnection &&
                  'enableCustodianCredentialsManager' in
                    defaultCustodianConnection &&
                  defaultCustodianConnection.enableCustodianCredentialsManager ===
                    true
                ) {
                  const { metadata } = defaultCustodianConnection;
                  const credsAppUrl = metadata?.find(
                    (m) => m.key === 'credentialsManagerUrl',
                  )?.value;
                  if (
                    typeof credsAppUrl === 'string' &&
                    credsAppUrl.trim() !== ''
                  ) {
                    const { protocol, host, pathname } = window.location;
                    const traceId = crypto
                      .randomUUID()
                      .substring(0, 12)
                      .replace(/-/g, '');
                    window.localStorage.setItem(
                      'custodianCredentialsManagerTraceId',
                      traceId,
                    );

                    const inboundRedirectUrl = `${protocol}//${host}${pathname}?custodianmeta=traceId:${traceId}`;
                    const toBeUrlEncoded = `redirect=${inboundRedirectUrl}`;
                    const outboundRedirectUrl = `${credsAppUrl}?${encodeURIComponent(toBeUrlEncoded)}`;
                    safeWindowOpen(outboundRedirectUrl, {
                      target: '_self',
                      onError: (errorMsg) => {
                        showToast({ severity: 'error', message: errorMsg });
                      },
                    });
                    return;
                  }
                }

                if (activeOrganization.loginUrl) {
                  safeWindowOpen(activeOrganization.loginUrl, {
                    target: '_self',
                    onError: (errorMsg) => {
                      showToast({ severity: 'error', message: errorMsg });
                    },
                  });
                } else {
                  setLoginSettingsOpen(true);
                }
              }}
            />
          )}
        {permissions.includes('write:organization_user_profile') && (
          <MenuItemWithIcon
            MuiSvgIcon={NotificationsNoneIcon}
            title={t('notifications')}
            testId='menuitem-notification-preferences'
            onClick={() => {
              handleClose();
              setUpdateNotificationPreferencesOpen(true);
            }}
          />
        )}
        {permissions.includes('write:organization_user_profile') && (
          <MenuItemWithIcon
            MuiSvgIcon={PersonIcon}
            title={t('updateAvatar')}
            testId='menuitem-updateAvatar'
            onClick={() => {
              handleClose();
              setUpdateAvatarOpen(true);
            }}
          />
        )}
        {activeOrganization.enableResourcesAndArticles && (
          <MenuItemWithIcon
            MuiSvgIcon={MenuBookIcon}
            SuffixMuiSvgIcon={LaunchIcon}
            title={t('resourcesArticles')}
            testId='menuitem-resourcesArticles'
            onClick={() => {
              const helpCentreUrl =
                translateBackend(activeOrganization?.translatedHelpCentreUrl) ||
                'https://onevest.zendesk.com/hc/en-us';
              handleUrlItem(
                ovAnalyticsEvents.homepageResourceArticlesSelect,
                helpCentreUrl,
              );
            }}
          />
        )}
        {activeOrganization.enableContactSupport && (
          <MenuItemWithIcon
            MuiSvgIcon={SupportAgentIcon}
            SuffixMuiSvgIcon={LaunchIcon}
            title={t('contactSupport')}
            testId='menuitem-contactSupport'
            onClick={() => {
              const supportUrl =
                translateBackend(activeOrganization?.translatedSupportUrl) ||
                'https://onevest.zendesk.com/hc/en-us/requests/new';
              handleUrlItem(
                ovAnalyticsEvents.homepageContactSupportSelect,
                supportUrl,
              );
            }}
          />
        )}
        {allowLogout && (
          <MenuItemWithIcon
            MuiSvgIcon={LogoutIcon}
            title={t('logout')}
            testId='menuitem-logout'
            onClick={() => {
              handleClose();
              logout();
            }}
          />
        )}
      </Menu>

      <LoginSettingModal
        open={loginSettingsOpen}
        setOpen={setLoginSettingsOpen}
      />
      <LanguageSelectionModal
        open={updateLanguageOpen}
        setOpen={setUpdateLanguageOpen}
      />
      <ChangeAvatarModal
        open={updateAvatarOpen}
        setOpen={setUpdateAvatarOpen}
      />
      <NotificationPreferencesModal
        open={updateNotificationPreferencesOpen}
        setOpen={setUpdateNotificationPreferencesOpen}
      />
      <CreateEntityWorkflow
        open={createEntityOpen}
        setOpen={setCreateEntityOpen}
      />
      <ViewAllProfilesDialog
        open={viewAllProfiles}
        onViewDetail={() => setViewAllProfiles(false)}
        onCancel={() => setViewAllProfiles(false)}
        onAddNewProfile={() => {
          setViewAllProfiles(false);
          setCreateEntityOpen(true);
        }}
      />
    </>
  );
};

export default EntityMenu;

const MenuItemWithImage = ({
  title,
  image,
  testId,
  onClick,
}: {
  title: string;
  image: string;
  testId: string;
  onClick: () => void;
}) => {
  const { sys } = useThemeTokens(useEntityMenuTokens());

  return (
    <MenuItem key={title} data-testid={testId} onClick={onClick}>
      <Box
        marginRight={sys.spacing.md}
        display='flex'
        height='32px'
        width='32px'
        alignItems='center'
        justifyContent='center'
      >
        <img src={image} alt='view-profile-icon' height='24px' width='24px' />
      </Box>
      <Typography variant='bodyMedium' fontWeight='500'>
        {title}
      </Typography>
    </MenuItem>
  );
};

const MenuItemWithIcon = ({
  title,
  MuiSvgIcon,
  SuffixMuiSvgIcon,
  testId,
  onClick,
}: {
  title: string;
  MuiSvgIcon: React.ElementType;
  SuffixMuiSvgIcon?: React.ElementType;
  testId: string;
  onClick: () => void;
}) => (
  <MenuItem
    data-testid={testId}
    key={testId}
    onClick={onClick}
    sx={{
      pv: 2,
      display: SuffixMuiSvgIcon ? 'flex' : undefined,
      justifyContent: SuffixMuiSvgIcon ? 'space-between' : undefined,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <MuiSvgIcon sx={{ mr: 1, fontSize: 18 }} />
      <Typography
        variant='bodyMedium'
        sx={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}
      >
        {title}
      </Typography>
    </Box>
    {SuffixMuiSvgIcon && <SuffixMuiSvgIcon sx={{ fontSize: 18 }} />}
  </MenuItem>
);

const EntityMenuItem = ({
  item,
  isActive,
  index,
  onClick,
}: {
  item: IUserEntity;
  isActive: boolean;
  index: number;
  onClick: () => void;
}) => {
  const { userContext } = useContext(UserContext);
  const { sys } = useThemeTokens(useEntityMenuTokens());
  const { t } = useTranslation(['entityMenu']);

  const profileName = useCallback(
    (e?: ActiveEntityProps) => entityName(e) || e?.primaryEmail,
    [],
  );
  const profileInitials = useCallback(
    (ActiveEntity?: ActiveEntityProps) => entityInitials(ActiveEntity),
    [],
  );

  const isAccessGranted =
    userContext.accessType === OrganizationUserAccessTypes.REP_CODE ||
    item?.relation !== OrganizationUserEntityRelationTypes.OWNER;
  const colorIndex = index % colors.length;

  return (
    <MenuItem
      key={item?.entity?.id}
      data-testid={`profile-${index}`}
      sx={{ pv: 2, display: 'flex', justifyContent: 'space-between' }}
      onClick={onClick}
    >
      <Box display='flex' alignItems='center'>
        <Box marginRight={sys.spacing.md} height='32px' width='32px'>
          <Avatar
            state='highlight'
            size='large'
            fgColor={colors[colorIndex]}
            bgColor={colorsInner[colorIndex]}
          >
            {profileInitials(item?.entity)}
          </Avatar>
        </Box>
        <Box>
          <Box display='flex' alignItems='center' justifyContent='center'>
            <Typography
              align='left'
              variant={isActive ? 'bodyLarge' : 'bodyMedium'}
              weight={isActive ? 'bold' : 'regular'}
              sx={{
                maxWidth: '140px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {profileName(item?.entity)}
            </Typography>
          </Box>
          {isAccessGranted && (
            <MemberTag
              sequenceNumber={colorIndex}
              name={t('entityMenu:accessGranted')}
            />
          )}
        </Box>
      </Box>
      {isActive && <CheckRoundedIcon style={{ fontSize: '16px' }} />}
    </MenuItem>
  );
};
